'use client'

import { useEffect, useState } from 'react'
import { type CarouselApi, CarouselContent, CarouselItem, Carousel as CarouselPrimitive, cn } from '@nwwp/ui-shadcn'

type CarouselProps<T> = {
  /** Items to render as slides. */
  items: T[]
  /** Stable React key for each item. */
  getKey: (item: T, index: number) => string
  /** Renders the slide content for each item. */
  renderItem: (item: T, index: number) => React.ReactNode
  /** Accessible label for each dot, e.g. (item) => `Go to ${item.name}`. */
  getDotLabel: (item: T, index: number) => string
  /** Noun used in arrow labels, e.g. "partner" → "Previous partner" / "Next partner". */
  controlNoun: string
  /** Tailwind width classes applied to each slide wrapper, e.g. 'w-[305px] tablet:w-[563px]'. */
  itemClassName?: string
  /** Whether the carousel wraps around. Defaults to true (circular). */
  loop?: boolean
  /** Slide alignment within the viewport. Defaults to 'start'. */
  align?: 'start' | 'center' | 'end'
  /** Stretch slides to equal height. Defaults to true. */
  stretch?: boolean
  /** Class applied to the outer wrapper. */
  className?: string
  'aria-label'?: string
}

export function Carousel<T>({
  items,
  getKey,
  renderItem,
  getDotLabel,
  controlNoun,
  itemClassName,
  loop = true,
  align = 'start',
  stretch = true,
  className,
  'aria-label': ariaLabel,
}: CarouselProps<T>) {
  const [api, setApi] = useState<CarouselApi>()
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!api) return

    const onSelect = () => setActiveIndex(api.selectedScrollSnap())
    onSelect()
    api.on('select', onSelect)
    api.on('reInit', onSelect)

    return () => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    }
  }, [api])

  return (
    <div className={cn('flex w-full flex-col gap-8', className)}>
      <CarouselPrimitive setApi={setApi} opts={{ align, loop }} aria-label={ariaLabel}>
        <CarouselContent data-scroller className="-ml-5">
          {items.map((item, index) => (
            <CarouselItem key={getKey(item, index)} className="basis-auto pl-5">
              <div className={cn(stretch && 'h-full', itemClassName)}>{renderItem(item, index)}</div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </CarouselPrimitive>

      {/* Nav: prev arrow · dots · next arrow */}
      <div className="flex items-center justify-center gap-8">
        <button
          type="button"
          aria-label={`Previous ${controlNoun}`}
          onClick={() => api?.scrollPrev()}
          className={cn(
            'flex size-10 items-center justify-center rounded-full',
            'border border-foreground/40 text-foreground',
            'transition-opacity hover:opacity-80'
          )}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 3L5 8L10 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={getKey(item, i)}
              data-dot
              type="button"
              aria-label={getDotLabel(item, i)}
              onClick={() => api?.scrollTo(i)}
              className={cn(
                'size-2 rounded-full transition-colors duration-300',
                i === activeIndex ? 'bg-green-lighter' : 'bg-foreground/30'
              )}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label={`Next ${controlNoun}`}
          onClick={() => api?.scrollNext()}
          className={cn(
            'flex size-10 items-center justify-center rounded-full',
            'border border-foreground/40 text-foreground',
            'transition-opacity hover:opacity-80'
          )}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6 3L11 8L6 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
